import * as React from "react";
import { CheckCircle, Warning, XCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  formatMoney, formatSignedMoney, denominationsTotal, depositBalanced,
} from "./utils";
import type { DayCloseLabels, DayCloseSnapshot } from "./types";

export interface VarianceReviewTableProps {
  snapshot: DayCloseSnapshot;
  onDepositChange: (closingFloatMinor: number, depositAmountMinor: number) => void;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  className?: string;
}

export function VarianceReviewTable({
  snapshot, onDepositChange, labels, locale, className,
}: VarianceReviewTableProps) {
  const { variance, deposit, cash, tolerance } = snapshot;
  const balanced = depositBalanced(snapshot);
  const cashCounted = denominationsTotal(cash.denominationCounts);

  return (
    <div className={cn("space-y-5", className)}>
      <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-biz-surface text-[11px] font-semibold uppercase tracking-wider text-biz-foreground-muted">
            <tr>
              <th className="text-left px-4 py-3">{labels.steps.non_cash.method}</th>
              <th className="text-right px-4 py-3">{labels.steps.review.variance}</th>
              <th className="text-right px-4 py-3">{labels.summary.expected}</th>
              <th className="text-right px-4 py-3">{labels.summary.counted}</th>
              <th className="text-right px-4 py-3">{labels.summary.variance}</th>
              <th className="text-right px-4 py-3">{labels.summary.status}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-biz-border">
            {variance.rows.map((row) => {
              const methodLabel =
                row.method === "cash" ? "Cash / Nakit" : labels.steps.non_cash.methods[row.method] ?? row.method;
              const hardBlock = Math.abs(row.varianceMinor) >= tolerance.hardBlockMinor;
              const Icon = row.withinTolerance ? CheckCircle : hardBlock ? XCircle : Warning;
              const tone = row.withinTolerance
                ? "text-biz-success"
                : hardBlock
                ? "text-biz-danger"
                : "text-biz-warning";
              return (
                <tr key={row.method} className="hover:bg-biz-muted/40">
                  <td className="px-4 py-3 font-semibold text-biz-foreground">{methodLabel}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={cn("inline-flex items-center gap-1 text-xs font-semibold", tone)}>
                      <Icon weight={row.withinTolerance ? "regular" : "fill"} className="w-3.5 h-3.5" />
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num tabular-nums text-biz-foreground-muted">
                    {formatMoney(row.expectedMinor, labels.currencyPrefix, locale)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num tabular-nums text-biz-foreground">
                    {formatMoney(row.countedMinor, labels.currencyPrefix, locale)}
                  </td>
                  <td className={cn("px-4 py-3 text-right font-mono-num tabular-nums font-semibold", tone)}>
                    {formatSignedMoney(row.varianceMinor, labels.currencyPrefix, locale)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border",
                        row.withinTolerance
                          ? "bg-biz-success-subtle border-biz-success-border text-biz-success"
                          : hardBlock
                          ? "bg-biz-danger-subtle border-biz-danger-border text-biz-danger"
                          : "bg-biz-warning-subtle border-biz-warning-border text-biz-warning",
                      )}
                    >
                      {row.withinTolerance
                        ? labels.steps.review.withinTolerance
                        : hardBlock
                        ? labels.steps.review.requiresOwnerOverride
                        : labels.steps.review.requiresManagerOverride}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-biz-surface">
            <tr>
              <td className="px-4 py-3 font-semibold text-biz-foreground">{labels.steps.review.total}</td>
              <td colSpan={4} />
              <td className="px-4 py-3 text-right font-mono-num tabular-nums font-bold text-biz-foreground">
                {formatMoney(variance.totalAbsMinor, labels.currencyPrefix, locale)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Deposit split */}
      <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-biz-foreground">{labels.steps.review.depositLabel}</h3>
          <span className="text-xs text-biz-foreground-muted">
            ({formatMoney(cashCounted, labels.currencyPrefix, locale)} {labels.summary.counted})
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <DepositField
            label={labels.steps.review.closingFloatLabel}
            valueMinor={deposit.closingFloatMinor}
            onChange={(v) => onDepositChange(v, deposit.depositAmountMinor)}
            currencyPrefix={labels.currencyPrefix}
          />
          <DepositField
            label={labels.steps.review.depositLabel}
            valueMinor={deposit.depositAmountMinor}
            onChange={(v) => onDepositChange(deposit.closingFloatMinor, v)}
            currencyPrefix={labels.currencyPrefix}
          />
        </div>
        <div
          className={cn(
            "flex items-center gap-2 text-xs",
            balanced ? "text-biz-success" : "text-biz-warning",
          )}
        >
          {balanced ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          <span>{labels.steps.review.depositHelp}</span>
        </div>
      </div>
    </div>
  );
}

function DepositField({
  label, valueMinor, onChange, currencyPrefix,
}: { label: string; valueMinor: number; onChange: (v: number) => void; currencyPrefix: string }) {
  const [text, setText] = React.useState((valueMinor / 100).toFixed(2));
  React.useEffect(() => setText((valueMinor / 100).toFixed(2)), [valueMinor]);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted font-mono-num pointer-events-none">
          {currencyPrefix}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
            setText(cleaned);
            const num = Number(cleaned || 0);
            if (!Number.isNaN(num)) onChange(Math.round(num * 100));
          }}
          className="h-12 w-full pl-8 pr-3 rounded-biz-btn border border-biz-border bg-biz-bg text-biz-foreground font-mono-num tabular-nums biz-focus-ring text-base"
        />
      </div>
    </label>
  );
}
